import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownToLine,
  Bell,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  Eye,
  FileCheck2,
  FileText,
  FolderOpen,
  HelpCircle,
  Home,
  Hourglass,
  KeyRound,
  LockKeyhole,
  LogOut,
  MessageCircle,
  Paperclip,
  ReceiptText,
  Send,
  Settings,
  ShieldCheck,
  UploadCloud,
  UserRoundCheck,
  X,
  XCircle,
} from 'lucide-react';
import api from '../services/api';
import { fetchCryptoUsdPrices, formatApproxUsd, formatTokenAmount } from '../utils/cryptoPrices';
import { usePortalLanguage, getPortalDateLocale, translatePortalStatus, portalT } from '../hooks/usePortalLanguage';
import { fetchPortalBranding, portalLogoUrl } from '../services/portalBranding';

const PORTAL_BASE = api.defaults.baseURL;

const portalApi = {
  get: (url) => api.get(url),
  post: (url, data) => api.post(url, data),
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(getPortalDateLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDT = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(getPortalDateLocale(), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const TABS = [
  { id: 'dashboard', labelKey: 'tabs.dashboard', icon: Home },
  { id: 'chat', labelKey: 'tabs.chat', icon: MessageCircle },
  { id: 'documents', labelKey: 'tabs.documents', icon: FileText },
  { id: 'timeline', labelKey: 'tabs.timeline', icon: CircleDashed },
  { id: 'settings', labelKey: 'tabs.settings', icon: Settings },
];

const VERIFY_TYPES = ['Passport', 'Driver License', 'ID Front', 'ID Back', 'Utility Bill', 'Bank Statement'];

const cx = (...classes) => classes.filter(Boolean).join(' ');

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'approved') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (normalized === 'declined') return 'bg-rose-50 text-rose-700 border-rose-100';
  if (normalized === 'pending') return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const StatusBadge = ({ status, fallback = 'Active', tone }) => {
  const label = status || fallback;
  const toneKey = tone || String(label).toLowerCase();
  const Icon = toneKey === 'approved' ? CheckCircle2 : toneKey === 'declined' ? XCircle : Clock3;

  return (
    <span className={cx(
      'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold',
      statusTone(toneKey),
    )}>
      <Icon className="h-3.5 w-3.5" />
      {translatePortalStatus(label)}
    </span>
  );
};

const PortalCard = ({ children, className = '' }) => (
  <div className={cx('rounded-lg border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]', className)}>
    {children}
  </div>
);

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm">
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-sm font-semibold text-slate-800">{title}</p>
    {body && <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">{body}</p>}
  </div>
);

const PortalSecureImage = ({ docId, alt, className }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = null;

    (async () => {
      try {
        const response = await fetch(`${PORTAL_BASE}/portal/documents/${docId}/download`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Image failed');
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err) {
        if (err.name !== 'AbortError') setSrc(null);
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId]);

  if (!src) {
    return <div className={cx('flex items-center justify-center bg-white text-xs font-semibold text-slate-400', className)}>Preview unavailable</div>;
  }

  return <img src={src} alt={alt} className={className} />;
};

const PortalDocumentPreview = ({ doc, onClose, onDownload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const ext = (doc?.fileName || '').split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
  const isPdf = ext === 'pdf';

  useEffect(() => {
    if (!doc) return undefined;
    const controller = new AbortController();
    let objectUrl = null;
    setLoading(true);
    setFailed(false);
    setPreviewUrl(null);

    (async () => {
      try {
        const response = await fetch(`${PORTAL_BASE}/portal/documents/${doc.id}/download`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Preview failed');
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (err.name !== 'AbortError') setFailed(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc]);

  if (!doc) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-950">{doc.fileName}</p>
            <p className="mt-0.5 text-xs text-slate-500">{doc.docType} / {fmtDate(doc.createdAt)}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => onDownload(`/portal/documents/${doc.id}/download`, doc.fileName)}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
              {portalT('common.download')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-950"
              aria-label={portalT('documents.closePreview')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-[60vh] flex-1 overflow-auto bg-slate-100 p-4">
          {loading && (
            <div className="flex min-h-[60vh] items-center justify-center text-sm font-semibold text-slate-500">
              {portalT('documents.loadingPreview')}
            </div>
          )}
          {!loading && failed && (
            <div className="flex min-h-[60vh] items-center justify-center text-center">
              <div>
                <FileText className="mx-auto h-10 w-10 text-slate-400" />
                <p className="mt-3 text-sm font-bold text-slate-900">{portalT('documents.previewUnavailable')}</p>
                <p className="mt-1 text-xs text-slate-500">{portalT('documents.previewUnavailableNote')}</p>
              </div>
            </div>
          )}
          {!loading && !failed && previewUrl && isImage && (
            <img src={previewUrl} alt={doc.fileName} className="mx-auto max-h-[72vh] max-w-full rounded-lg bg-white object-contain shadow-sm" />
          )}
          {!loading && !failed && previewUrl && isPdf && (
            <iframe title={doc.fileName} src={previewUrl} className="h-[72vh] w-full rounded-lg border border-slate-200 bg-white" />
          )}
          {!loading && !failed && previewUrl && !isImage && !isPdf && (
            <div className="flex min-h-[60vh] items-center justify-center text-center">
              <div>
                <FileText className="mx-auto h-10 w-10 text-slate-400" />
                <p className="mt-3 text-sm font-bold text-slate-900">{portalT('documents.previewUnsupported')}</p>
                <p className="mt-1 text-xs text-slate-500">{portalT('documents.previewUnsupportedNote')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function PortalDashboard() {
  const navigate = useNavigate();
  const { language, t, toggleLanguage, applyClientDefault } = usePortalLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clientInfo, setClientInfo] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [unread, setUnread] = useState({ messages: 0, timeline: 0 });
  const [branding, setBranding] = useState({ brandName: null, hasLogo: false });

  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatFile, setChatFile] = useState(null);
  const chatFileRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatPollRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [docSubTab, setDocSubTab] = useState('verification');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const verifyFileRef = useRef(null);
  const [verifyDocType, setVerifyDocType] = useState('Passport');
  const verifyDocTypeRef = useRef('Passport');
  const caseFileRef = useRef(null);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  const [caseInfo, setCaseInfo] = useState({ assets: [] });
  const [cryptoPrices, setCryptoPrices] = useState({});

  const verificationDocs = docs.filter(d => VERIFY_TYPES.includes(d.docType));
  const approvedCount = verificationDocs.filter(d => d.status === 'approved').length;
  const declinedCount = verificationDocs.filter(d => d.status === 'declined').length;
  const pendingCount = docs.filter(d => d.status === 'pending').length;
  const missingVerificationCount = VERIFY_TYPES.filter(type => !verificationDocs.some(doc => doc.docType === type)).length;
  const lastUpdate = events[0]?.eventDate || events[0]?.createdAt || docs[0]?.createdAt;
  // Real agent-set milestone from the CRM (0 / 15 / 50 / 75 / 100) — not a heuristic.
  const progress = caseInfo.progress || 0;
  const caseStageLabel = t(`progressStages.${caseInfo.stage || 'none'}`);
  const activeTabConfig = TABS.find(tab => tab.id === activeTab) || TABS[0];
  const fallbackNextStepLabel = declinedCount > 0
    ? t('dashboard.fallbackReplaceDeclined')
    : missingVerificationCount > 0
      ? t('dashboard.fallbackUploadMissing')
      : pendingCount > 0
        ? t('dashboard.fallbackWaitReview')
        : t('dashboard.fallbackReviewUpdate');
  const nextStepLabel = caseInfo.nextStep?.title || fallbackNextStepLabel;
  const nextStepDescription = caseInfo.nextStep?.description;
  const caseBlocker = caseInfo.blocker;
  const caseBlockerTitle = caseBlocker?.waitingOn === 'client'
    ? t('blocker.waitingClient')
    : caseBlocker?.waitingOn === 'us'
      ? t('blocker.waitingUs')
      : caseBlocker?.waitingOn === 'third_party'
        ? t('blocker.waitingThirdParty')
        : t('blocker.onHold');
  // Client-facing case status: hold overrides, otherwise derived from the stage.
  const caseStatus = caseBlocker?.text
    ? { label: t('status.onHold'), tone: 'pending' }
    : caseInfo.stage === 'completed'
      ? { label: t('status.closed'), tone: 'approved' }
      : caseInfo.stage === 'funds_located' || caseInfo.stage === 'payout_in_progress'
        ? { label: t(`progressStages.${caseInfo.stage}`), tone: 'approved' }
        : caseInfo.stage === 'documents_confirmed'
          ? { label: t('status.inProgress'), tone: undefined }
          : { label: t('status.opened'), tone: undefined };
  const verificationByType = VERIFY_TYPES.map(type => ({
    type,
    docs: verificationDocs.filter(doc => doc.docType === type),
  }));
  const clientName = clientInfo?.name || 'Client';
  const newestDocs = docs.slice(0, 4);
  const latestMessages = messages.slice(-3).reverse();
  const visibleTimeline = events;
  const currentTimelineEvent = visibleTimeline[visibleTimeline.length - 1];
  const timelineTitle = currentTimelineEvent?.source || currentTimelineEvent?.caseStatus || t('dashboard.noCaseEventsYet');
  const matterCards = [
    {
      title: clientInfo?.caseTitle || t('dashboard.legalMatter'),
      status: 'In progress',
      lawyer: clientInfo?.agent || t('dashboard.assignedTeam'),
      steps: [
        { label: 'Documents received', done: verificationDocs.length > 0 },
        { label: 'Identity review', done: approvedCount > 0 },
        { label: 'Legal review in progress', done: events.length > 0 },
        { label: 'Next milestone scheduled', done: false },
      ],
    },
  ];

  useEffect(() => {
    loadMe();
    loadTimeline();
    loadDocuments();
    loadCaseInfo();
    loadUnread();
    fetchPortalBranding().then(setBranding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCryptoUsdPrices().then(setCryptoPrices).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'timeline') loadTimeline();
    if (activeTab === 'dashboard' || activeTab === 'chat') { loadMessages(); startChatPoll(); }
    if (activeTab === 'dashboard' || activeTab === 'documents') loadDocuments();
    if (activeTab === 'dashboard' || activeTab === 'timeline') loadCaseInfo();
    if (activeTab === 'chat') {
      portalApi.post('/portal/messages/mark-read').catch(() => {});
      setUnread(prev => ({ ...prev, messages: 0 }));
    } else if (activeTab === 'timeline') {
      portalApi.post('/portal/timeline/mark-seen').catch(() => {});
      setUnread(prev => ({ ...prev, timeline: 0 }));
    } else {
      loadUnread();
    }
    return () => stopChatPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadMe = async () => {
    try {
      const { data } = await portalApi.get('/portal/me');
      setClientInfo(data);
      applyClientDefault(data?.language);
    } catch { navigate('/portal'); }
  };

  const loadTimeline = async () => {
    setEventsLoading(true);
    try {
      const { data } = await portalApi.get('/portal/timeline');
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    }
    finally { setEventsLoading(false); }
  };

  const loadMessages = async ({ incremental = false } = {}) => {
    try {
      const params = incremental && lastMessageIdRef.current
        ? `?after_id=${encodeURIComponent(lastMessageIdRef.current)}&limit=50`
        : '?limit=50';
      const { data } = await portalApi.get(`/portal/messages${params}`);
      if (!Array.isArray(data) || data.length === 0) return;
      lastMessageIdRef.current = data[data.length - 1].id;
      setMessages(prev => {
        const merged = incremental ? [...prev, ...data] : data;
        const unique = Array.from(new Map(merged.map(m => [m.id, m])).values());
        return unique.slice(-200);
      });
    } catch {}
  };

  const loadDocuments = async () => {
    try {
      const { data } = await portalApi.get('/portal/documents');
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      setDocs([]);
    }
  };

  const loadCaseInfo = async () => {
    try { const { data } = await portalApi.get('/portal/case-management'); setCaseInfo(data || { assets: [] }); } catch {}
  };

  const loadUnread = async () => {
    try {
      const { data } = await portalApi.get('/portal/unread');
      setUnread({ messages: data?.messages || 0, timeline: data?.timeline || 0 });
    } catch {}
  };

  const uploadPortalDoc = async (file, docType) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploadingDoc(true);
    try {
      await api.post(`/portal/documents/upload?doc_type=${encodeURIComponent(docType)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadDocuments();
    } catch (err) {
      alert(err?.response?.data?.detail || t('documents.uploadFailed'));
    } finally { setUploadingDoc(false); }
  };

  const startChatPoll = () => {
    stopChatPoll();
    chatPollRef.current = setInterval(() => {
      if (document.hidden) return;
      loadMessages({ incremental: true });
    }, 10000);
  };

  const stopChatPoll = () => {
    if (chatPollRef.current) clearInterval(chatPollRef.current);
    chatPollRef.current = null;
  };

  const downloadPortalFile = async (url, filename = 'download') => {
    const response = await fetch(`${PORTAL_BASE}${url}`, {
      credentials: 'include',
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);
    if (pwForm.next !== pwForm.confirm) { setPwError(t('settings.passwordsDontMatch')); return; }
    if (pwForm.next.length < 6) { setPwError(t('settings.newPlaceholder')); return; }
    setPwLoading(true);
    try {
      await portalApi.post('/portal/change-password', { current_password: pwForm.current, new_password: pwForm.next });
      setPwSuccess(true);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwError(err?.response?.data?.detail || t('settings.changeFailed'));
    } finally { setPwLoading(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgInput.trim() && !chatFile) return;
    setSendingMsg(true);
    try {
      let data;
      if (chatFile) {
        const fd = new FormData();
        fd.append('body', msgInput.trim());
        fd.append('file', chatFile);
        const res = await api.post('/portal/messages/upload', fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        data = res.data;
      } else {
        const res = await portalApi.post('/portal/messages', { body: msgInput.trim() });
        data = res.data;
      }
      lastMessageIdRef.current = data.id;
      setMessages(prev => [...prev, data].slice(-200));
      setMsgInput('');
      setChatFile(null);
      setChatError('');
      if (chatFileRef.current) chatFileRef.current.value = '';
    } catch (err) {
      setChatError(err?.response?.data?.detail || t('chat.sendFailed'));
    } finally { setSendingMsg(false); }
  };

  const logout = async () => {
    try {
      await api.post('/portal/logout');
    } catch {}
    navigate('/portal');
  };

  const renderDocumentRows = (rows, accent = 'slate', canDownload = false) => {
    if (rows.length === 0) {
      return <EmptyState icon={FolderOpen} title={t('documents.emptyTitle')} body={t('documents.emptyBody')} />;
    }

    const accentMap = {
      violet: 'bg-violet-50 text-violet-700',
      emerald: 'bg-emerald-50 text-emerald-700',
      blue: 'bg-blue-50 text-blue-700',
      slate: 'bg-slate-100 text-slate-700',
    };

    return (
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {rows.map(doc => (
          <div key={doc.id} className="flex items-center gap-3 px-4 py-3.5">
            <div className={cx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', accentMap[accent])}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{doc.fileName}</p>
              <p className="mt-0.5 text-xs text-slate-500">{doc.docType} / {fmtDate(doc.createdAt)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setPreviewDoc(doc)}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                <Eye className="h-3.5 w-3.5" />
                {t('common.preview')}
              </button>
              {canDownload ? (
                <button
                  type="button"
                  onClick={() => downloadPortalFile(`/portal/documents/${doc.id}/download`, doc.fileName)}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  <ArrowDownToLine className="h-3.5 w-3.5" />
                  {t('common.download')}
                </button>
              ) : (
                <StatusBadge status={doc.status} fallback="Uploaded" />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRecoveredFunds = (compact = false) => {
    const assets = caseInfo.assets || [];

    if (assets.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-500">
          {t('funds.emptyState')}
        </div>
      );
    }

    return (
      <div className={cx('grid gap-3', compact ? '' : 'sm:grid-cols-2')}>
        {assets.map(asset => (
          <div key={asset.id} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                {asset.type === 'crypto' ? t('funds.cryptoLocated') : t('funds.bankLocated')}
              </span>
              {asset.foundAt && <span className="shrink-0 text-xs font-semibold text-slate-600">{fmtDate(asset.foundAt)}</span>}
            </div>
            {asset.type === 'crypto' ? (
              <>
                {asset.cryptocurrency && <p className="text-sm font-bold text-slate-950">{asset.cryptocurrency}</p>}
                {asset.amount != null && (
                  <p className="mt-1 text-2xl font-bold text-emerald-700">
                    {formatTokenAmount(asset.amount, asset.cryptocurrency)}
                    {formatApproxUsd(asset.amount, asset.cryptocurrency, cryptoPrices) && (
                      <span className="ml-2 text-sm font-semibold text-slate-500">{formatApproxUsd(asset.amount, asset.cryptocurrency, cryptoPrices)}</span>
                    )}
                  </p>
                )}
                {asset.source && <p className="mt-2 text-xs text-slate-600"><span className="font-semibold">{t('funds.source')}</span> {asset.source}</p>}
                {asset.blockchainLink && (
                  <a href={asset.blockchainLink} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-xs font-semibold text-violet-700 hover:underline">
                    {t('funds.viewBlockchain')}
                  </a>
                )}
              </>
            ) : (
              <>
                {asset.bank && <p className="text-sm font-bold text-slate-950">{asset.bank}</p>}
                {asset.amount != null && <p className="mt-1 text-2xl font-bold text-emerald-700">{asset.amount} {asset.currency || ''}</p>}
                {asset.account && <p className="mt-2 text-xs text-slate-600"><span className="font-semibold">{t('funds.account')}</span> {asset.account}</p>}
                {asset.source && <p className="mt-1 text-xs text-slate-600"><span className="font-semibold">{t('funds.source')}</span> {asset.source}</p>}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-950">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {branding.hasLogo ? (
              <img src={portalLogoUrl()} alt={branding.brandName || t('header.title')} className="h-10 max-w-[120px] shrink-0 rounded-lg object-contain" />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-white shadow-lg shadow-slate-950/15">
                <ShieldCheck className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-950">{branding.brandName || t('header.title')}</p>
              <p className="truncate text-xs font-medium text-slate-500">{clientInfo?.name || t('header.subtitle')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 sm:inline-flex">
              <LockKeyhole className="h-3.5 w-3.5" />
              {t('header.workspaceBadge')}
            </span>
            {clientInfo && <StatusBadge status={caseStatus.label} tone={caseStatus.tone} />}
            <button
              onClick={toggleLanguage}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950"
              title={language === 'en' ? 'Переключить на русский' : 'Switch to English'}
            >
              {language === 'en' ? 'RU' : 'EN'}
            </button>
            <button
              onClick={logout}
              className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('common.signOut')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-4 lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)]">
          <PortalCard className="overflow-hidden">
            <div className="bg-slate-950 p-5 text-white">
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/80">{t('sidebar.activeCase')}</span>
              <p className="mt-5 text-xl font-bold leading-tight">{matterCards[0].title}</p>
              <p className="mt-2 text-sm leading-5 text-slate-300">{t('sidebar.caseSubtitle')}</p>
            </div>
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-500">
                <span>{t('sidebar.caseReadiness')}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100">
                <div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className={cx('mt-2 text-xs font-semibold', progress === 100 ? 'text-emerald-600' : 'text-slate-500')}>
                {caseStageLabel}
              </p>
              <button
                onClick={() => setActiveTab(declinedCount || missingVerificationCount ? 'documents' : 'chat')}
                className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white"
              >
                {declinedCount || missingVerificationCount ? t('sidebar.uploadDocuments') : t('sidebar.messageLegalTeam')}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </PortalCard>

          <nav className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              const badgeCount = tab.id === 'chat' ? unread.messages : tab.id === 'timeline' ? unread.timeline : 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cx(
                    'flex h-11 w-full items-center justify-between rounded-lg px-3 text-sm font-semibold transition-colors',
                    active ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {t(tab.labelKey)}
                  </span>
                  <span className="flex items-center gap-2">
                    {badgeCount > 0 && !active && (
                      <span className="min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                    {active && <ChevronRight className="h-4 w-4" />}
                  </span>
                </button>
              );
            })}
          </nav>

          <PortalCard className="p-4">
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                <UserRoundCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{t('sidebar.needHelp')}</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">{t('sidebar.needHelpBody')}</p>
              </div>
            </div>
          </PortalCard>
        </aside>

        <section className="min-w-0 space-y-6">
          {caseBlocker?.text && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm sm:p-5">
              <div className="flex gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <Hourglass className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-amber-900">{caseBlockerTitle}</p>
                  <p className="mt-1 text-sm leading-6 text-amber-800">{caseBlocker.text}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {caseBlocker.eta && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800">
                        <Clock3 className="h-3.5 w-3.5" />
                        {t('blocker.estimated', { eta: caseBlocker.eta })}
                      </span>
                    )}
                    {caseBlocker.waitingOn === 'client' && activeTab !== 'chat' && (
                      <button
                        onClick={() => setActiveTab('chat')}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-amber-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                      >
                        {t('blocker.messageLegalTeam')}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'dashboard' && (
            <>
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
                <PortalCard className="overflow-hidden">
                  <div className="border-b border-slate-200 bg-white p-5 sm:p-6">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-2xl">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">{t('dashboard.caseOverview')}</p>
                        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-950">{t('dashboard.hello', { name: clientName })}</h1>
                        <p className="mt-2 text-base font-semibold text-slate-700">{t('dashboard.underReview', { title: matterCards[0].title })}</p>
                        <p className="mt-3 text-sm leading-6 text-slate-500">{t('dashboard.overviewIntro')}</p>
                      </div>
                      <div
                        className={cx(
                          'rounded-lg border-2 p-4 shadow-sm lg:w-64',
                          caseBlocker?.text ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50'
                        )}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className={cx('text-xs font-bold uppercase tracking-[0.12em]', caseBlocker?.text ? 'text-amber-700' : 'text-blue-700')}>
                            {t('dashboard.nextAction')}
                          </p>
                          {caseBlocker?.text && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              <Hourglass className="h-3 w-3" />
                              {t('status.onHold')}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-base font-extrabold leading-6 text-slate-900">{nextStepLabel}</p>
                        {nextStepDescription && (
                          <p className={cx('mt-2 text-xs leading-5', caseBlocker?.text ? 'text-amber-800' : 'text-slate-500')}>{nextStepDescription}</p>
                        )}
                        {caseBlocker?.text && caseBlocker.eta && (
                          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800">
                            <Clock3 className="h-3.5 w-3.5" />
                            {t('blocker.estimated', { eta: caseBlocker.eta })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-0 divide-y divide-slate-200 md:grid-cols-4 md:divide-x md:divide-y-0">
                    {[
                      { label: t('dashboard.readiness'), value: `${progress}%`, detail: caseStageLabel, icon: CheckCircle2, color: 'text-emerald-700 bg-emerald-50' },
                      { label: t('dashboard.documents'), value: docs.length, detail: t('dashboard.verifiedCount', { count: approvedCount }), icon: FileCheck2, color: 'text-blue-700 bg-blue-50' },
                      { label: t('dashboard.messages'), value: messages.length, detail: latestMessages[0] ? t('dashboard.latestAt', { date: fmtDT(latestMessages[0].createdAt) }) : t('dashboard.noMessagesYet'), icon: MessageCircle, color: 'text-violet-700 bg-violet-50' },
                      { label: t('dashboard.locatedFunds'), value: (caseInfo.assets || []).length, detail: t('dashboard.locatedFundsDetail'), icon: ReceiptText, color: 'text-amber-700 bg-amber-50' },
                    ].map(item => {
                      const Icon = item.icon;
                      return (
                        <div key={item.label} className="p-4">
                          <div className={cx('mb-3 flex h-10 w-10 items-center justify-center rounded-lg', item.color)}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <p className="text-2xl font-extrabold text-slate-950">{item.value}</p>
                          <p className="mt-1 text-sm font-bold text-slate-700">{item.label}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{item.detail}</p>
                        </div>
                      );
                    })}
                  </div>
                </PortalCard>

                <PortalCard className="p-5">
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">{t('dashboard.caseTimeline')}</p>
                      <h2 className="mt-1 text-lg font-extrabold text-slate-950">{timelineTitle}</h2>
                      <p className="mt-1 text-xs leading-5 text-slate-500">{t('dashboard.timelineNote')}</p>
                    </div>
                    {currentTimelineEvent && <StatusBadge status={currentTimelineEvent.caseStatus || 'In progress'} />}
                  </div>

                  {visibleTimeline.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-500">
                      {t('dashboard.noVisibleTimeline')}
                    </p>
                  ) : (
                    <div className="space-y-0">
                      {visibleTimeline.slice(-5).map((event, index, steps) => {
                        const isCurrent = index === steps.length - 1;
                        const label = event.source || event.caseStatus || t('dashboard.caseUpdate');
                        const body = event.comment || event.caseStatus || t('dashboard.timelineEventBody');
                        return (
                          <div key={event.id || `${label}-${index}`} className="relative grid grid-cols-[34px_minmax(0,1fr)] gap-3 pb-5 last:pb-0">
                            {index < steps.length - 1 && (
                              <div className="absolute left-4 top-8 h-[calc(100%-1.25rem)] w-px bg-emerald-200" />
                            )}
                            <div className={cx(
                              'relative z-10 flex h-8 w-8 items-center justify-center rounded-full border',
                              isCurrent
                                ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-[0_0_0_4px_rgba(37,99,235,0.08)]'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700',
                            )}>
                              {isCurrent ? <CircleDashed className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                            </div>
                            <div className={cx(
                              'rounded-lg border p-3',
                              isCurrent ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50',
                            )}>
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-sm font-bold text-slate-950">{label}</p>
                                <span className={cx(
                                  'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em]',
                                  isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700',
                                )}>
                                  {isCurrent ? t('common.now') : t('common.done')}
                                </span>
                              </div>
                              <p className="mt-1 text-xs leading-5 text-slate-500">{body}</p>
                              <p className="mt-2 text-xs font-semibold text-slate-400">{fmtDate(event.eventDate || event.createdAt)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </PortalCard>
              </div>

              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(300px,0.75fr)]">
                <PortalCard className="p-5">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.caseActivity')}</p>
                      <h2 className="mt-1 text-xl font-extrabold text-slate-950">{t('dashboard.whatChanged')}</h2>
                    </div>
                    <button onClick={() => setActiveTab('timeline')} className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-600 hover:bg-slate-50">
                      {t('dashboard.viewProgress')}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  {eventsLoading ? (
                    <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-500">{t('dashboard.loadingActivity')}</p>
                  ) : events.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-500">{t('dashboard.noPublishedUpdates')}</p>
                  ) : (
                    <div className="space-y-3">
                      {events.slice(0, 4).map((event, index) => (
                        <div key={event.id || `${event.title}-${index}`} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-4">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                            <Bell className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-950">{event.source || event.caseStatus || t('dashboard.caseUpdate')}</p>
                            <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">{event.comment || event.caseStatus || t('dashboard.newTimelineUpdate')}</p>
                            <p className="mt-2 text-xs font-semibold text-slate-400">{fmtDate(event.eventDate || event.createdAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </PortalCard>

                <div className="space-y-5">
                  <PortalCard className="p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-extrabold text-slate-950">{t('dashboard.documents')}</h2>
                      <button onClick={() => setActiveTab('documents')} className="text-xs font-bold text-blue-700 hover:text-blue-900">{t('dashboard.viewAll')}</button>
                    </div>
                    {newestDocs.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">{t('dashboard.noDocsYet')}</p>
                    ) : newestDocs.slice(0, 3).map(doc => (
                      <div key={doc.id} className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-950">{doc.fileName}</p>
                          <p className="text-xs text-slate-500">{doc.docType} / {fmtDate(doc.createdAt)}</p>
                        </div>
                        <StatusBadge status={doc.status} fallback="New" />
                      </div>
                    ))}
                  </PortalCard>

                  <PortalCard className="p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-extrabold text-slate-950">{t('dashboard.messages')}</h2>
                      <button onClick={() => setActiveTab('chat')} className="text-xs font-bold text-blue-700 hover:text-blue-900">{t('dashboard.openChat')}</button>
                    </div>
                    {latestMessages.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">{t('dashboard.noMessagesYet')}</p>
                    ) : latestMessages.slice(0, 2).map(msg => (
                      <div key={msg.id} className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs font-semibold text-slate-500">{msg.sender === 'client' ? t('dashboard.you') : msg.author || t('dashboard.legalTeam')} / {fmtDT(msg.createdAt)}</p>
                        <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-800">{msg.body}</p>
                      </div>
                    ))}
                  </PortalCard>
                </div>
              </div>

              <PortalCard className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-extrabold text-slate-950">{t('dashboard.locatedFunds')}</h2>
                  <button onClick={() => setActiveTab('timeline')} className="text-xs font-bold text-blue-700 hover:text-blue-900">{t('dashboard.details')}</button>
                </div>
                {renderRecoveredFunds(false)}
              </PortalCard>
            </>
          )}

          {activeTab !== 'dashboard' && (
          <PortalCard className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{t('common.clientPortal')}</p>
                <p className="mt-1 text-lg font-bold text-slate-950">{t(activeTabConfig.labelKey)}</p>
              </div>
              <button onClick={() => setActiveTab('dashboard')} className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-600 hover:bg-slate-50">
                {t('common.backToOverview')}
              </button>
            </div>
          </PortalCard>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-6">
              <PortalCard className="p-5 sm:p-6">
                <div className="mb-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">{t('timeline.locatedFunds')}</p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-950">{t('timeline.fundsTitle')}</h1>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{t('timeline.fundsNote')}</p>
                </div>
                {renderRecoveredFunds(false)}
              </PortalCard>

              <PortalCard className="p-5 sm:p-6">
                <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-600">{t('timeline.progressLabel')}</p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-950">{t('timeline.updatesTitle')}</h1>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{t('timeline.updatesNote')}</p>
                  </div>
                </div>

                {eventsLoading ? (
                  <div className="py-20 text-center text-sm font-medium text-slate-500">{t('timeline.loadingUpdates')}</div>
                ) : events.length === 0 ? (
                  <EmptyState
                    icon={CircleDashed}
                    title={t('timeline.emptyTitle')}
                    body={t('timeline.emptyBody')}
                  />
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-3 bottom-3 w-px bg-slate-200" />
                    <div className="space-y-4">
                      {events.map((ev, i) => (
                        <div key={ev.id} className="relative flex gap-4">
                          <div className={cx(
                            'z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-4 border-white shadow-sm',
                            i === 0 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500',
                          )}>
                            {i === 0 ? <CheckCircle2 className="h-4 w-4" /> : <CircleDashed className="h-4 w-4" />}
                          </div>
                          <div className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                              <p className="text-sm font-bold text-slate-950">{ev.caseStatus}</p>
                              <p className="text-xs font-semibold text-slate-500">{ev.eventDate || fmtDate(ev.createdAt)}</p>
                            </div>
                            {ev.source && <p className="mt-1 text-xs font-semibold text-violet-700">{ev.source}</p>}
                            {ev.comment && <p className="mt-3 text-sm leading-6 text-slate-600">{ev.comment}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </PortalCard>
            </div>
          )}

          {activeTab === 'chat' && (
            <PortalCard className="flex h-[calc(100vh-11rem)] min-h-[560px] flex-col overflow-hidden">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-600">{t('chat.confidential')}</p>
                    <h1 className="mt-1 text-xl font-bold text-slate-950">{t('chat.title')}</h1>
                    <p className="mt-1 text-sm text-slate-500">{t('chat.subtitle')}</p>
                  </div>
                  <StatusBadge status="Active" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-slate-50 p-5">
                {messages.length === 0 && (
                  <EmptyState
                    icon={MessageCircle}
                    title={t('chat.emptyTitle')}
                    body={t('chat.emptyBody')}
                  />
                )}
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div key={msg.id} className={cx('flex', msg.sender === 'client' ? 'justify-end' : 'justify-start')}>
                      <div className={cx(
                        'max-w-[82%] rounded-lg px-4 py-3 text-sm leading-6 shadow-sm',
                        msg.sender === 'client'
                          ? 'bg-slate-950 text-white'
                          : 'border border-slate-200 bg-white text-slate-800',
                      )}>
                        <p>{msg.body}</p>
                        {msg.attachment && (
                          <button
                            type="button"
                            onClick={() => downloadPortalFile(`/portal/messages/${msg.id}/attachment`, msg.attachment.fileName)}
                            className={cx(
                              'mt-3 flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition-colors',
                              msg.sender === 'client'
                                ? 'border-white/20 bg-white/10 text-white hover:bg-white/15'
                                : 'border-amber-300/30 bg-amber-300/10 text-amber-200 hover:bg-amber-300/15',
                            )}
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                            <span className="truncate">{msg.attachment.fileName}</span>
                          </button>
                        )}
                        <p className={cx('mt-2 text-xs', msg.sender === 'client' ? 'text-slate-300' : 'text-slate-400')}>
                          {fmtDT(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={sendMessage} className="border-t border-slate-200 bg-white p-4">
                {chatError && (
                  <div className="mb-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                    {chatError}
                  </div>
                )}
                {chatFile && (
                  <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs font-semibold text-amber-100">
                    <span className="flex min-w-0 items-center gap-2">
                      <Paperclip className="h-4 w-4 shrink-0" />
                      <span className="truncate">{chatFile.name}</span>
                    </span>
                    <button type="button" onClick={() => setChatFile(null)} className="text-amber-200 hover:text-white">{t('common.remove')}</button>
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => chatFileRef.current?.click()}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
                    title={t('chat.attachFile')}
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    ref={chatFileRef}
                    type="file"
                    className="hidden"
                    onChange={e => setChatFile(e.target.files?.[0] || null)}
                  />
                  <input
                    value={msgInput}
                    onChange={e => setMsgInput(e.target.value)}
                    placeholder={t('chat.placeholder')}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-slate-200 px-3.5 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  />
                  <button
                    type="submit"
                    disabled={sendingMsg || (!msgInput.trim() && !chatFile)}
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('common.send')}</span>
                  </button>
                </div>
              </form>
            </PortalCard>
          )}

          {activeTab === 'documents' && (
            <PortalCard className="p-5 sm:p-6">
              <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-600">{t('documents.centerLabel')}</p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-950">{t('documents.title')}</h1>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{t('documents.subtitle')}</p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                  <FileCheck2 className="h-3.5 w-3.5" />
                  {t('documents.verifiedBadge', { count: approvedCount })}
                </span>
              </div>

              <div className="mb-6 flex gap-2 overflow-x-auto rounded-lg bg-slate-100 p-1">
                {[
                  { id: 'verification', label: t('documents.verification') },
                  { id: 'case', label: t('documents.caseFiles') },
                  { id: 'files', label: t('documents.files') },
                  { id: 'invoices', label: t('documents.invoices') },
                ].map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setDocSubTab(sub.id)}
                    className={cx(
                      'h-10 shrink-0 rounded-md px-4 text-sm font-semibold transition-colors',
                      docSubTab === sub.id ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950',
                    )}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              {docSubTab === 'verification' && (
                <div className="space-y-4">
                  <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:grid-cols-[minmax(0,1fr)_auto]">
                    <div>
                      <p className="text-sm font-bold text-slate-950">{t('documents.identityTitle')}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">{t('documents.identityNote')}</p>
                    </div>
                    <div className="flex min-w-0 gap-2">
                      <select
                        value={verifyDocType}
                        onChange={e => {
                          setVerifyDocType(e.target.value);
                          verifyDocTypeRef.current = e.target.value;
                        }}
                        className="h-10 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      >
                        {VERIFY_TYPES.map(type => <option key={type} value={type}>{t(`verifyTypes.${type}`)}</option>)}
                      </select>
                      <button
                        onClick={() => verifyFileRef.current?.click()}
                        disabled={uploadingDoc}
                        className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <UploadCloud className="h-4 w-4" />
                        {uploadingDoc ? t('common.uploading') : t('common.upload')}
                      </button>
                      <input
                        ref={verifyFileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={e => { const f = e.target.files[0]; if (f) uploadPortalDoc(f, verifyDocTypeRef.current); e.target.value = ''; }}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    {verificationByType.map(({ type, docs: typeDocs }) => {
                      const latest = typeDocs[0];
                      const status = latest?.status;
                      const isApproved = status === 'approved';
                      const isDeclined = status === 'declined';
                      const isPending = status === 'pending';
                      const isImage = latest && /\.(jpg|jpeg|png|webp)$/i.test(latest.fileName || '');
                      return (
                        <div key={type} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-bold text-slate-950">{t(`verifyTypes.${type}`)}</p>
                              <p className="mt-1 text-xs leading-5 text-slate-500">
                                {latest ? `${latest.fileName} / ${fmtDate(latest.createdAt)}` : t('documents.notUploadedYet')}
                              </p>
                            </div>
                            {latest ? <StatusBadge status={latest.status} /> : <StatusBadge status="Missing" />}
                          </div>

                          <div className={cx(
                            'flex min-h-[150px] items-center justify-center rounded-lg border border-dashed p-3',
                            isApproved ? 'border-emerald-200 bg-emerald-50' : isDeclined ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white',
                          )}>
                            {latest ? (
                              isImage ? (
                                <PortalSecureImage
                                  docId={latest.id}
                                  alt={type}
                                  className="max-h-36 w-full rounded-md object-contain"
                                />
                              ) : (
                                <div className="text-center">
                                  <FileText className="mx-auto h-9 w-9 text-slate-500" />
                                  <p className="mt-2 text-xs font-semibold text-slate-600">{latest.fileName}</p>
                                </div>
                              )
                            ) : (
                              <div className="text-center">
                                <UploadCloud className="mx-auto h-8 w-8 text-slate-400" />
                                <p className="mt-2 text-xs font-semibold text-slate-500">{t('documents.awaitingUpload')}</p>
                              </div>
                            )}
                          </div>

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                            <p className={cx(
                              'text-xs font-semibold',
                              isApproved ? 'text-emerald-700' : isDeclined ? 'text-rose-700' : isPending ? 'text-amber-700' : 'text-slate-500',
                            )}>
                              {isApproved && t('documents.approvedNote')}
                              {isDeclined && t('documents.declinedNote')}
                              {isPending && t('documents.pendingNote')}
                              {!latest && t('documents.uploadToContinue')}
                            </p>
                            <div className="flex gap-2">
                              {latest && (
                                <button
                                  type="button"
                                  onClick={() => setPreviewDoc(latest)}
                                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                  {t('common.view')}
                                </button>
                              )}
                              {(!isApproved || isDeclined) && (
                                <button
                                  onClick={() => {
                                    setVerifyDocType(type);
                                    verifyDocTypeRef.current = type;
                                    verifyFileRef.current?.click();
                                  }}
                                  disabled={uploadingDoc}
                                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white disabled:opacity-50"
                                >
                                  <UploadCloud className="h-3.5 w-3.5" />
                                  {latest ? t('common.replace') : t('common.upload')}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {docSubTab === 'case' && (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-950">{t('documents.caseTeamFilesTitle')}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">{t('documents.caseTeamFilesNote')}</p>
                    </div>
                    <button
                      onClick={() => caseFileRef.current?.click()}
                      disabled={uploadingDoc}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UploadCloud className="h-4 w-4" />
                      {uploadingDoc ? t('common.uploading') : t('documents.uploadFile')}
                    </button>
                    <input
                      ref={caseFileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.zip"
                      className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f) uploadPortalDoc(f, 'case_file'); e.target.value = ''; }}
                    />
                  </div>
                  {renderDocumentRows(docs.filter(d => d.docType === 'case_file'), 'emerald')}
                </div>
              )}

              {docSubTab === 'files' && renderDocumentRows(docs.filter(d => d.docType === 'file'), 'blue', true)}
              {docSubTab === 'invoices' && renderDocumentRows(docs.filter(d => d.docType === 'invoice'), 'violet', true)}
            </PortalCard>
          )}

          {activeTab === 'settings' && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,520px)_minmax(280px,1fr)]">
              <PortalCard className="p-5 sm:p-6">
                <div className="mb-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-600">{t('settings.protection')}</p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-950">{t('settings.title')}</h1>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{t('settings.subtitle')}</p>
                </div>
                <form onSubmit={changePassword} className="space-y-4">
                  {[
                    { key: 'current', label: t('settings.currentPassword'), placeholder: t('settings.currentPlaceholder') },
                    { key: 'next', label: t('settings.newPassword'), placeholder: t('settings.newPlaceholder') },
                    { key: 'confirm', label: t('settings.confirmPassword'), placeholder: t('settings.confirmPlaceholder') },
                  ].map(field => (
                    <label key={field.key} className="block">
                      <span className="mb-1.5 block text-xs font-bold text-slate-600">{field.label}</span>
                      <input
                        type="password"
                        required
                        value={pwForm[field.key]}
                        onChange={e => setPwForm(p => ({ ...p, [field.key]: e.target.value }))}
                        className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder={field.placeholder}
                      />
                    </label>
                  ))}
                  {pwError && <p className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{pwError}</p>}
                  {pwSuccess && <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{t('settings.changed')}</p>}
                  <button
                    type="submit"
                    disabled={pwLoading}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <KeyRound className="h-4 w-4" />
                    {pwLoading ? t('settings.saving') : t('settings.update')}
                  </button>
                </form>
              </PortalCard>

              <PortalCard className="p-5 sm:p-6">
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                    <HelpCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-950">{t('settings.guidanceTitle')}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{t('settings.guidanceBody')}</p>
                  </div>
                </div>
              </PortalCard>
            </div>
          )}
        </section>
      </main>
      {previewDoc && (
        <PortalDocumentPreview
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={downloadPortalFile}
        />
      )}
    </div>
  );
}
